import firebase from "firebase/compat/app";
import { isWebOtpEnabled } from "./firebase/twoFactorAuthConfig";

export const POST_LOGIN_PHONE_SKIP_KEY = "vs-post-login-phone-skipped";

export function normalizeRealmIds(claims: Record<string, unknown>): string[] {
  const raw = claims.__realms;
  let list: unknown[] | null = null;
  if (Array.isArray(raw)) {
    list = raw;
  } else if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        list = parsed;
      }
    } catch {
      list = null;
    }
  }
  if (!list) {
    return [];
  }
  return list.filter((x): x is string => typeof x === "string");
}

export function resolveCharityIdFromClaims(
  claims: Record<string, unknown>,
  charityFromRedux: string
): string {
  if (charityFromRedux) {
    return charityFromRedux;
  }
  const target = claims.targetRealm;
  if (typeof target === "string" && target.length > 0) {
    return target;
  }
  const realms = normalizeRealmIds(claims);
  const first = realms.find(x => x !== "superadmin" && x !== "donation-reader");
  return typeof first === "string" ? first : "";
}

export function resolveAdminIdFromClaims(
  userId: string,
  claims: Record<string, unknown>,
  firebaseUid: string
): string {
  if (userId && String(userId).trim()) {
    return String(userId).trim();
  }
  const realId = claims.realId;
  if (typeof realId === "string" && realId.length > 0) {
    return realId;
  }
  return firebaseUid;
}

export interface AdminPhonePromptContext {
  charityId: string;
  adminId: string;
  firebaseUid: string;
}

export async function resolveAdminPhonePromptContext(
  firebaseAuthUser: firebase.User,
  adminUserId: string,
  charityFromRedux: string
): Promise<AdminPhonePromptContext | null> {
  let claims: Record<string, unknown> = {};
  try {
    const tr = await firebaseAuthUser.getIdTokenResult(false);
    claims = (tr.claims || {}) as Record<string, unknown>;
  } catch {
    /* redux-only resolution */
  }
  const charityId = resolveCharityIdFromClaims(claims, charityFromRedux);
  const adminId = resolveAdminIdFromClaims(
    adminUserId,
    claims,
    firebaseAuthUser.uid
  );
  if (!charityId || !adminId) {
    return null;
  }
  return {
    charityId,
    adminId,
    firebaseUid: firebaseAuthUser.uid,
  };
}

export async function adminHasVerifiedPhone(
  db: firebase.database.Database,
  charityId: string,
  adminId: string
): Promise<boolean> {
  try {
    const snap = await db
      .ref(`${charityId}/users/${adminId}/phoneNumber`)
      .once("value");
    const raw = snap.val();
    const digits = raw != null ? String(raw).replace(/\D/g, "") : "";
    return digits.length >= 10;
  } catch {
    return false;
  }
}

export async function isPhoneReminderPermanentlyDismissed(
  db: firebase.database.Database,
  firebaseUid: string
): Promise<boolean> {
  try {
    const snap = await db
      .ref(`global/userSettings/${firebaseUid}/dontRemindPhone`)
      .once("value");
    return snap.val() === true;
  } catch {
    return false;
  }
}

export function skipPostLoginPhoneScreen(): void {
  sessionStorage.setItem(POST_LOGIN_PHONE_SKIP_KEY, "1");
}

export function clearPostLoginPhoneScreenSkip(): void {
  try {
    sessionStorage.removeItem(POST_LOGIN_PHONE_SKIP_KEY);
  } catch {
    /* sessionStorage unavailable */
  }
}

export async function shouldShowPostLoginPhoneScreen(
  firebaseApi: Pick<firebase.app.App, "auth" | "database">,
  adminUserId: string,
  charityFromRedux: string
): Promise<boolean> {
  if (sessionStorage.getItem(POST_LOGIN_PHONE_SKIP_KEY) === "1") {
    return false;
  }
  if (!(await isWebOtpEnabled())) {
    return false;
  }
  if (typeof firebaseApi.database !== "function") {
    return false;
  }
  const u = firebaseApi.auth().currentUser;
  if (!u) {
    return false;
  }
  const ctx = await resolveAdminPhonePromptContext(
    u,
    adminUserId,
    charityFromRedux
  );
  if (!ctx) {
    return false;
  }
  const db = firebaseApi.database();
  const [dismissed, hasPhone] = await Promise.all([
    isPhoneReminderPermanentlyDismissed(db, ctx.firebaseUid),
    adminHasVerifiedPhone(db, ctx.charityId, ctx.adminId),
  ]);
  return !dismissed && !hasPhone;
}

export async function redirectAfterLoginWithPhoneGate(
  navigate: (path: string) => void,
  firebaseApi: Pick<firebase.app.App, "auth" | "database">,
  adminUserId: string,
  destinationPath: string,
  charityFromRedux: string
): Promise<void> {
  clearPostLoginPhoneScreenSkip();
  const show = await shouldShowPostLoginPhoneScreen(
    firebaseApi,
    adminUserId,
    charityFromRedux
  );
  if (!show) {
    navigate(destinationPath);
    return;
  }
  navigate(
    `/setup-phone?returnTo=${encodeURIComponent(destinationPath)}`
  );
}
